'use client'

import { useCallback, useState } from 'react'

import api from '@/lib/api'

import { useEmailForm, useOtpForm } from './reportLostDeviceForm'

export type ReportLostDeviceStep = 'email' | 'otp' | 'done'

/**
 * IEU.07 Cas 1 — orchestrates the self-service lost/stolen-device flow. The user
 * is re-authenticated by a degraded factor (email OTP) before the account is
 * disabled, so an attacker cannot lock out an arbitrary account.
 *
 * `step` is flow state; `error` carries server-side failures (validation errors
 * live on the RHF forms).
 */
export function useReportLostDevice() {
  const [step, setStep] = useState<ReportLostDeviceStep>('email')
  const [error, setError] = useState<string | null>(null)

  const emailForm = useEmailForm()
  const otpForm = useOtpForm()

  const normalizedEmail = () => emailForm.getValues('email').trim().toLowerCase()

  const sendCode = emailForm.handleSubmit(async ({ email }) => {
    setError(null)
    try {
      await api.auth.reportLostDeviceStart(email.trim().toLowerCase())
      // Always advances (no account enumeration): the OTP is only actually sent
      // when an account with that email exists.
      setStep('otp')
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.')
    }
  })

  const confirm = otpForm.handleSubmit(async ({ otp }) => {
    setError(null)
    try {
      const res = await api.auth.reportLostDeviceConfirm(normalizedEmail(), otp)
      if (res.status === 'ok') {
        setStep('done')
        return
      }
      setError('Le code saisi est invalide ou a expiré. Veuillez réessayer.')
    } catch {
      setError('Une erreur est survenue. Veuillez réessayer.')
    }
  })

  const resend = useCallback(async () => {
    setError(null)
    otpForm.reset({ otp: '' })
    try {
      await api.auth.reportLostDeviceStart(normalizedEmail())
    } catch {
      setError('Renvoi impossible. Réessayez dans une minute.')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailForm, otpForm])

  return { step, error, email: normalizedEmail(), emailForm, otpForm, sendCode, confirm, resend }
}
